import type { QueryClient } from '@tanstack/react-query'
import { bindersQueries } from '@/hooks/binders/binderQueries'
import type { BinderResponse, BindersResponse, BinderSlotResponse, BinderSummary } from '@/types'

type BinderSlot = BinderSlotResponse['slot']

function toSummary(resp: BinderResponse): BinderSummary {
  const b = resp.binder
  const firstPage = b.pages[0]
  const cover = firstPage?.slots.find((s) => s.position === 0)?.card ?? null
  const slotCount = b.pages.reduce((sum, page) => sum + page.slots.length, 0)
  return {
    id: b.id,
    name: b.name,
    description: b.description,
    layout: b.layout,
    coverColor: b.coverColor,
    pageCount: b.pages.length,
    slotCount,
    coverCard: cover,
    shareToken: b.shareToken,
    shareUrl: b.shareUrl,
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
  }
}

export function appendToList(queryClient: QueryClient, resp: BinderResponse) {
  queryClient.setQueryData<BindersResponse>(bindersQueries.list.queryKey(), (old) => {
    if (!old) return { binders: [toSummary(resp)] }
    return { binders: [toSummary(resp), ...old.binders] }
  })
}

export function replaceInList(queryClient: QueryClient, resp: BinderResponse) {
  queryClient.setQueryData<BindersResponse>(bindersQueries.list.queryKey(), (old) => {
    if (!old) return old
    const summary = toSummary(resp)
    return { binders: old.binders.map((b) => (b.id === summary.id ? summary : b)) }
  })
}

export function removeFromList(queryClient: QueryClient, id: number) {
  queryClient.setQueryData<BindersResponse>(bindersQueries.list.queryKey(), (old) => {
    if (!old) return old
    return { binders: old.binders.filter((b) => b.id !== id) }
  })
}

export function withDetailRefresh(queryClient: QueryClient, id: number) {
  queryClient.invalidateQueries({ queryKey: bindersQueries.detail.queryKey(id) })
}

/** Patch the share token/url on both the cached detail and the cached list row. */
export function patchShareToken(queryClient: QueryClient, id: number, shareToken: string, shareUrl: string) {
  queryClient.setQueryData<BinderResponse>(bindersQueries.detail.queryKey(id), (old) => {
    if (!old) return old
    return { binder: { ...old.binder, shareToken, shareUrl } }
  })
  queryClient.setQueryData<BindersResponse>(bindersQueries.list.queryKey(), (old) => {
    if (!old) return old
    return { binders: old.binders.map((b) => (b.id === id ? { ...b, shareToken, shareUrl } : b)) }
  })
}

/** Replace (or insert) a slot on one page of the cached binder detail, keeping slots position-ordered. */
export function upsertSlot(
  queryClient: QueryClient,
  id: number,
  pageNumber: number,
  position: number,
  slot: BinderSlot,
) {
  queryClient.setQueryData<BinderResponse>(bindersQueries.detail.queryKey(id), (old) => {
    if (!old) return old
    return {
      binder: {
        ...old.binder,
        pages: old.binder.pages.map((page) => {
          if (page.pageNumber !== pageNumber) return page
          const others = page.slots.filter((s) => s.position !== position)
          return { ...page, slots: [...others, slot].sort((a, b) => a.position - b.position) }
        }),
      },
    }
  })
}

/** Drop the slot at (pageNumber, position) from the cached binder detail. */
export function removeSlot(queryClient: QueryClient, id: number, pageNumber: number, position: number) {
  queryClient.setQueryData<BinderResponse>(bindersQueries.detail.queryKey(id), (old) => {
    if (!old) return old
    return {
      binder: {
        ...old.binder,
        pages: old.binder.pages.map((page) =>
          page.pageNumber === pageNumber ? { ...page, slots: page.slots.filter((s) => s.position !== position) } : page,
        ),
      },
    }
  })
}
